import React, { createRef, Fragment, useRef } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { filterSpacer } from '../../../utils/filter-spacer';
import { MetadataPopupButton } from '../../metadata-popup-button';
import { Spacer } from '../../metadata-spacer';
import { useOverflowObserver } from '../../overflow-observer';

/** A metadata part to render */
export type SearchMetadataPart = string | null | [string, React.ReactNode];

const styles = cssMap({
	subheading: {
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		gridArea: 'subheading',
		display: 'flex',
		alignItems: 'center',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		paddingLeft: token('space.050'),
	},
	inline: {
		flex: '1 0 auto',
		position: 'absolute',
		zIndex: 100,
		alignItems: 'center',
	},
	hiddenReferenceInline: {
		flex: '1 0 auto',
		visibility: 'hidden',
		position: 'relative',
		zIndex: 1,
		alignItems: 'center',
	},
	metadataItem: {
		display: 'inline-flex',
		alignItems: 'center',
	},
});

const renderParts = (part: SearchMetadataPart, spacer: boolean) => {
	const [key, value] = Array.isArray(part) ? part : [part, part];
	return (
		<Fragment key={key}>
			{value}
			{spacer && <Spacer />}
		</Fragment>
	);
};

const filterPart = (part: SearchMetadataPart): boolean => {
	if (Array.isArray(part)) {
		return !!part[1];
	}
	return !!part;
};

export const filterPartsByKeys = (
	parts: SearchMetadataPart[],
	allowedKeys: string[],
): SearchMetadataPart[] => {
	return parts.filter((part) => {
		if (Array.isArray(part)) {
			const [key] = part;
			return allowedKeys.includes(key);
		}
		// For string parts, we keep them as they don't have keys
		return typeof part === 'string';
	});
};

const SearchMetadataWithOverflowObserver = ({
	metadataItems,
	searchResultId,
	containerRef,
}: {
	metadataItems: React.ReactNode[];
	searchResultId?: string;
	containerRef: React.MutableRefObject<HTMLDivElement | null>;
}) => {
	const headerMetadataWithIds = metadataItems.map((item, index) => {
		const prefix = searchResultId || 'default';
		return {
			id: `metadata-${prefix}-${index}`,
			content: item,
		};
	});

	const headerMetadataRefs = useRef<React.MutableRefObject<HTMLDivElement>[]>([]);

	headerMetadataRefs.current = headerMetadataWithIds.map(
		(_, i) => headerMetadataRefs.current[i] ?? createRef(),
	);

	const visibleItems = useOverflowObserver({
		containerRef,
		itemRefs: headerMetadataRefs,
		itemIds: headerMetadataWithIds.map((item) => item.id),
	});

	const hasHiddenItems = headerMetadataWithIds.length > visibleItems.length;

	const processVisibleItems = () => {
		const visibleMetadata = headerMetadataWithIds.filter((item) => visibleItems.includes(item.id));

		// If we have hidden items, remove spacer from last visible item
		if (hasHiddenItems && visibleMetadata.length > 0) {
			const lastIndex = visibleMetadata.length - 1;
			visibleMetadata[lastIndex] = {
				...visibleMetadata[lastIndex],
				content: filterSpacer(visibleMetadata[lastIndex].content),
			};
		}

		return visibleMetadata;
	};

	const processedVisibleItems = processVisibleItems();

	return (
		<Box xcss={styles.subheading} testId="search-page-linked-result-metadata">
			<Inline
				xcss={styles.hiddenReferenceInline}
				testId="search-page-linked-result-metadata-hidden"
			>
				{headerMetadataWithIds.map((item, index) => {
					const elementId = `reference:${item.id}`;
					return (
						<Box
							key={elementId}
							id={elementId}
							ref={headerMetadataRefs.current[index]}
							xcss={styles.metadataItem}
						>
							{item.content}
						</Box>
					);
				})}
			</Inline>
			<Inline xcss={styles.inline} testId="search-page-linked-result-metadata-visible">
				{processedVisibleItems.map((item) => (
					<Box key={`visible-${item.id}`} xcss={styles.metadataItem}>
						{item.content}
					</Box>
				))}
				{hasHiddenItems && (
					<MetadataPopupButton
						metadata={headerMetadataWithIds
							.filter((item) => !visibleItems.includes(item.id))
							.map((item) => item.content)}
						isFirstElement={processedVisibleItems.length === 0}
					/>
				)}
			</Inline>
		</Box>
	);
};

export type SearchMetadataProps = {
	searchResultId?: string;
	// TODO: This was previously ProductType
	// We need to align our ProductKeys types with
	// import type { ProductType } from '@atlassian/search-client';
	// For now, lets drop to string and re-cast
	product?: string;
	productSelected?: boolean;
	/** Nullish/empty string parts are filtered out. */
	parts?: Array<SearchMetadataPart>;
	socialParts?: Array<SearchMetadataPart>;
	displaySpacer?: boolean;
	// Using the presence of containerRef to indicate whether to use overflow observer (mainly for search result header metadata)
	containerRef?: React.MutableRefObject<HTMLDivElement | null>;
};

export const LinkedResultMetadata = ({
	searchResultId,
	parts: defaultParts = [],
	displaySpacer = true,
	containerRef,
}: SearchMetadataProps): React.JSX.Element => {
	// Filter to only keep parts with keys 'type', 'last-modified' and 'video-duration'
	const filteredParts = filterPartsByKeys(defaultParts, [
		'type',
		'last-modified',
		'video-duration',
	]);
	const parts = filteredParts.filter(filterPart);

	const metadataItems = parts.map((m, i) => renderParts(m, displaySpacer && i < parts.length - 1));

	// If we don't have a containerRef, we don't want to use the overflow observer
	return containerRef ? (
		<SearchMetadataWithOverflowObserver
			metadataItems={metadataItems}
			searchResultId={searchResultId}
			containerRef={containerRef}
		/>
	) : (
		<>{metadataItems}</>
	);
};
